import 'twin.macro';
import React, { useRef } from 'react';
import { v4 as uuid } from 'uuid';
import AutoSuggestGroup from '@teetsh/app/src/components/Form/AutoSuggestGroup';
import { Item } from '@teetsh/app/src/components/Form/AutoSuggestGroup/types';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { Option } from '@teetsh/app/src/components/Form/AutoSuggest';
import { SuggestionItem } from './SuggestionItem';
import tw, { css } from 'twin.macro';
import { PlusIcon } from '@heroicons/react/24/outline';
import FadeInComponent from '@teetsh/app/src/components/Transitions/FadeIn';
import { useTranslation } from 'react-i18next';

export interface Props {
  id: string;
  options: Option[];
  items: Item[];
  label: string;
  placeholder?: string;
  addButtonLabel: string;
  className?: string;
  onChange: (v: Item[]) => void;
  singleValue?: boolean;
  displayLength?: boolean;
  noFill?: boolean;
  itemIdFocused?: string;
  suggestion?: string;
  hint?: React.ReactNode;
  onSuggestionClick?: (v: string) => void;
  children?: React.ReactNode;
}

export default function MultiOptionsCreatable({
  items = [],
  onChange,
  options,
  singleValue,
  label,
  placeholder,
  displayLength = false,
  addButtonLabel,
  suggestion,
  hint,
  children,
  onSuggestionClick,
  className = '',
}: Props) {
  const lastInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation(['common']);

  if (items.length > 0) {
    return (
      <AutoSuggestGroup
        items={items}
        onChange={onChange as (v: (Item | string)[]) => void}
        singleValue={singleValue}
        className={className}
        inputRef={lastInputRef}
        placeholder={placeholder}
        options={options}
        label={label}
      >
        {children}
      </AutoSuggestGroup>
    );
  }
  return (
    <div tw="flex items-center gap-2.5">
      <Button
        onClick={() => {
          onChange([...items, { id: uuid(), value: '' }]);
          setTimeout(() => {
            lastInputRef?.current?.focus();
          }, 0);
        }}
        intent="primary"
        outlined
        alignText="left"
        small
        icon={PlusIcon}
        css={[
          css`
            &:has(.suggestion-item:hover) {
              ${tw`bg-transparent`}
            }
          `,
          tw`shrink-0`,
        ]}
      >
        <span tw="shrink-0">{addButtonLabel}</span>
      </Button>
      {hint ? (
        <FadeInComponent delay={800}>{hint}</FadeInComponent>
      ) : (
        <>
          {displayLength && options.length > 0 && (
            <div tw="text-neutral-quiet font-normal text-sm min-w-0">
              <span tw="hidden sm:block truncate">
                {t('common:components.bo.xAvailableSuggestions', {
                  count: options.length,
                })}
              </span>
              <span tw="block sm:hidden truncate">
                {t('common:components.bo.xSuggestions', {
                  count: options.length,
                })}
              </span>
            </div>
          )}
          {suggestion && (
            <SuggestionItem
              className="suggestion-item"
              value={suggestion}
              onChange={v => {
                if (onSuggestionClick) {
                  onSuggestionClick?.(suggestion);
                }
                onChange([...items, { id: uuid(), value: v }]);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
